import React from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import SectionHead from "../components/SectionHead";

const ease = [0.16, 1, 0.3, 1];

const PILLAR_GLYPHS = [
  // protect — three concentric arcs (a shield, abstracted)
  (
    <svg viewBox="0 0 60 60" key="a" aria-hidden="true" className="pillar-glyph">
      <path d="M30 6 L52 16 V32 C52 44 42 52 30 56 C18 52 8 44 8 32 V16 Z" fill="none" stroke="currentColor" strokeWidth="0.8" />
      <path d="M30 14 L46 20 V32 C46 41 38 47 30 50 C22 47 14 41 14 32 V20 Z" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.6" />
    </svg>
  ),
  // recover — a curve rising from a low point
  (
    <svg viewBox="0 0 60 60" key="b" aria-hidden="true" className="pillar-glyph">
      <path d="M6 46 C 18 46 22 28 30 28 C 38 28 42 14 54 14" fill="none" stroke="currentColor" strokeWidth="0.8" />
      <circle cx="6" cy="46" r="1.2" fill="currentColor" />
      <circle cx="54" cy="14" r="1.2" fill="currentColor" />
    </svg>
  ),
  // refine — series of thinning hairlines
  (
    <svg viewBox="0 0 60 60" key="c" aria-hidden="true" className="pillar-glyph">
      <line x1="6" y1="14" x2="54" y2="14" stroke="currentColor" strokeWidth="1.6" />
      <line x1="6" y1="24" x2="54" y2="24" stroke="currentColor" strokeWidth="1.1" />
      <line x1="6" y1="34" x2="54" y2="34" stroke="currentColor" strokeWidth="0.7" />
      <line x1="6" y1="44" x2="54" y2="44" stroke="currentColor" strokeWidth="0.4" />
    </svg>
  ),
];

export default function Principles() {
  const { t } = useI18n();
  return (
    <section
      id="principles"
      className="section border-t hair"
      data-testid="principles-section"
    >
      <div className="container-edge">
        <SectionHead
          roman={t.sections.principles.roman}
          right={<span className="mono" style={{ color: "var(--faded)" }}>fig. 02</span>}
        />

        <motion.h2
          className="display text-[44px] sm:text-[60px] md:text-[88px] lg:text-[112px] whitespace-pre-line mb-20 md:mb-28 max-w-[16ch]"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease }}
          data-testid="principles-heading"
        >
          {t.principles.heading}
        </motion.h2>

        <div className="grid grid-cols-12 gap-y-12 md:gap-x-8 border-t hair">
          {t.principles.items.map((p, idx) => (
            <motion.article
              key={p.n}
              className="col-span-12 md:col-span-4 border-b hair md:border-b-0 md:border-r md:hair pt-10 pb-10 md:pr-8 last:md:border-r-0"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.85, ease, delay: idx * 0.08 }}
              data-testid={`principle-${idx + 1}`}
            >
              <div className="flex items-center justify-between mb-10 md:mb-12">
                <span className="mono" style={{ color: "var(--faded)" }}>
                  {p.n}
                </span>
                <div className="pillar-glyph-wrap" style={{ color: "var(--ink)", width: 48, height: 48 }}>
                  {PILLAR_GLYPHS[idx]}
                </div>
              </div>
              <h3
                className="display text-[34px] md:text-[44px] mb-5"
                style={{ fontWeight: 400 }}
              >
                {p.name}
              </h3>
              <p className="body-text" style={{ maxWidth: "30ch" }}>
                {p.body}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
